// ============================================================
// GST E-INVOICE JSON EXPORT (schema v1.1)
// Matches the exact structure your current process's GST-portal
// uploads use — verified against real sample files.
// ============================================================
import { stateCode } from "./indianStates";

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// invoice_date is stored as ISO (YYYY-MM-DD); the schema requires DD/MM/YYYY.
function toDocDate(isoDate) {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  return `${d}/${m}/${y}`;
}

// `lines` — [{ product_name, hsn_code, tax_rate, quantity, unit_price, line_total, unit }]
// line_total is the tax-inclusive (gross) amount, matching how invoice
// totals are already computed elsewhere in the app (CreateInvoiceModal /
// InvoicePrintView) — AssAmt here is derived from it the same way.
export function buildEInvoiceJSON({ invoice, company, customer, lines }) {
  const buyerStateCode = invoice.place_of_supply_code || stateCode(customer.state) || "";
  const isInterstate   = !!invoice.is_interstate;

  let assVal = 0, cgstVal = 0, sgstVal = 0, igstVal = 0;

  const itemList = lines.map((l, i) => {
    const rate  = parseFloat(l.tax_rate) || 0;
    const gross = parseFloat(l.line_total) || 0;
    const assAmt = round2(gross * 100 / (100 + rate));
    const qty = parseFloat(l.quantity) || 1;
    const unitPrice = round2(qty ? assAmt / qty : assAmt);

    let cgstAmt = 0, sgstAmt = 0, igstAmt = 0;
    if (isInterstate) igstAmt = round2(assAmt * rate / 100);
    else { cgstAmt = round2(assAmt * rate / 200); sgstAmt = round2(assAmt * rate / 200); }

    assVal += assAmt; cgstVal += cgstAmt; sgstVal += sgstAmt; igstVal += igstAmt;

    return {
      SlNo: String(i + 1),
      PrdDesc: l.product_name,
      IsServc: "N",
      HsnCd: l.hsn_code || "",
      Qty: qty,
      Unit: (l.unit || "nos").toUpperCase(),
      UnitPrice: unitPrice,
      TotAmt: assAmt,
      Discount: 0,
      PreTaxVal: 0,
      AssAmt: assAmt,
      GstRt: rate,
      IgstAmt: igstAmt,
      CgstAmt: cgstAmt,
      SgstAmt: sgstAmt,
      CesRt: 0, CesAmt: 0, CesNonAdvlAmt: 0,
      StateCesRt: 0, StateCesAmt: 0, StateCesNonAdvlAmt: 0,
      OthChrg: 0,
      TotItemVal: Math.round(assAmt + cgstAmt + sgstAmt + igstAmt),
    };
  });

  assVal = round2(assVal); cgstVal = round2(cgstVal); sgstVal = round2(sgstVal); igstVal = round2(igstVal);
  const rawTotal  = assVal + cgstVal + sgstVal + igstVal;
  const totInvVal = Math.round(rawTotal);
  const rndOffAmt = round2(totInvVal - rawTotal);

  return [{
    Version: "1.1",
    TranDtls: { TaxSch: "GST", SupTyp: "B2B", IgstOnIntra: "N", RegRev: null, EcmGstin: null },
    DocDtls: { Typ: "INV", No: String(invoice.invoice_number), Dt: toDocDate(invoice.invoice_date) },
    SellerDtls: {
      Gstin: company.gstin,
      LglNm: company.name,
      TrdNm: null,
      Addr1: company.addr1,
      Addr2: null,
      Loc: company.loc,
      Pin: Number(company.pin),
      Stcd: company.stateCode,
      Ph: company.phone || null,
      Em: company.email || null,
    },
    BuyerDtls: {
      Gstin: customer.gstin,
      LglNm: customer.name,
      TrdNm: customer.name,
      Pos: buyerStateCode,
      Addr1: customer.address_line1 || "",
      Addr2: customer.address_line2 || null,
      Loc: customer.city || "",
      Pin: customer.pincode ? Number(customer.pincode) : null,
      Stcd: buyerStateCode,
      Ph: customer.phone || null,
      Em: customer.email || null,
    },
    ValDtls: {
      AssVal: assVal,
      IgstVal: igstVal,
      CgstVal: cgstVal,
      SgstVal: sgstVal,
      CesVal: 0,
      StCesVal: 0,
      Discount: 0,
      OthChrg: 0,
      RndOffAmt: rndOffAmt,
      TotInvVal: totInvVal,
    },
    ItemList: itemList,
  }];
}

export function downloadEInvoiceJSON(payload, filename) {
  const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
